/**
 * Cadastros → Feriados e dias sem expediente: os nacionais já estão no sistema
 * (dominio/feriados.ts); aqui entram os municipais, pontes e dias em que a
 * distribuidora não fatura. Cada um tira um dia útil do mês: muda o ritmo e a
 * projeção do painel na hora.
 *
 * Lê: os cadastros (feriados extras). Grava: api.salvarFeriado e api.removerFeriado.
 * Usado por: telas/Cadastros.tsx.
 */
import { useState } from 'react';
import { api } from '../../dados/api.ts';
import { FERIADOS } from '../../dominio/feriados.ts';
import type { Feriado } from '../../dominio/tipos.ts';
import { formatarData } from '../../formatacao.ts';
import type { Executar } from './comum.ts';

const DIAS_DA_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
/** "2026-10-24" → "sábado" (sem fuso: a data é só a data). */
const diaDaSemana = (data: string) => DIAS_DA_SEMANA[new Date(`${data}T12:00:00Z`).getUTCDay()] ?? '';

export function Feriados({ feriados, executar }: { feriados: Feriado[]; executar: Executar }) {
  const [data, setData] = useState('');
  const [nome, setNome] = useState('');

  async function salvar() {
    if (await executar(() => api.salvarFeriado(data, nome.trim()), `${formatarData(data)} (${nome.trim()}) cadastrado: não conta como dia útil.`)) {
      setData('');
      setNome('');
    }
  }

  function remover(feriado: Feriado) {
    if (!window.confirm(`Tirar ${formatarData(feriado.data)} (${feriado.nome})? O dia volta a contar como dia útil.`)) return;
    void executar(() => api.removerFeriado(feriado.data), `${formatarData(feriado.data)} voltou a ser dia útil.`);
  }

  return (
    <section className="cartao pilha">
      <div>
        <h2>Feriados e dias sem expediente</h2>
        <p className="subtitulo">Os feriados nacionais já contam. Cadastre aqui os municipais, pontes e dias sem faturamento: eles saem dos dias úteis do mês (ritmo e projeção).</p>
      </div>
      <div className="antes-depois">
        <label>
          Data
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
        </label>
        <label>
          Nome
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="ex.: Aniversário da cidade" />
        </label>
        <button type="button" className="botao" onClick={() => void salvar()} disabled={data === '' || nome.trim() === ''}>
          Cadastrar
        </button>
      </div>
      {feriados.length === 0 ? (
        <p className="texto-apagado">Nenhum feriado extra cadastrado.</p>
      ) : (
        <ul className="lista-ligacoes">
          {feriados.map((feriado) => (
            <li key={feriado.data}>
              <strong>{formatarData(feriado.data)}</strong> <span className="texto-apagado">({diaDaSemana(feriado.data)})</span> {feriado.nome}{' '}
              <button type="button" className="botao-link" onClick={() => remover(feriado)}>
                remover
              </button>
            </li>
          ))}
        </ul>
      )}
      <details>
        <summary>Feriados nacionais que o sistema já conta</summary>
        <ul className="lista-ligacoes texto-pequeno">
          {FERIADOS.map((feriado) => (
            <li key={feriado.data}>
              {formatarData(feriado.data)} <span className="texto-apagado">({diaDaSemana(feriado.data)})</span> {feriado.nome}
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
